import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Save, LogOut, Plus, Trash2, Pencil, Upload, X, Loader2, ExternalLink, Home,
  Copy, FileText, Image as ImageIcon, Film, BarChart3, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { applyTheme } from "@/components/ThemeInjector";
import { SortableList, SortableRow } from "@/components/admin/Sortable";
import { resolveMedia } from "@/lib/media";

const DEFAULT_THEME = {
  bg: "#08080a", surface: "#0e0e11", surface_2: "#0b0b0d",
  text: "#f4f4f5", muted: "#9a9aa0",
  accent: "#ff6b00", accent_soft: "#ff8c3a", accent_2: "#ff3d00", on_accent: "#0a0a0c",
};

const TABS = [
  { id: "general", label: "General" },
  { id: "theme", label: "Colores" },
  { id: "hero", label: "Inicio" },
  { id: "manifesto", label: "Manifiesto" },
  { id: "sections", label: "Secciones" },
  { id: "media", label: "Video" },
  { id: "products", label: "Productos" },
  { id: "downloads", label: "Descargas" },
  { id: "files", label: "Medios" },
  { id: "analytics", label: "Analítica" },
  { id: "account", label: "Cuenta" },
];

const MAX_UPLOAD_MB = 25;
const ALLOWED_EXT = ["jpg", "jpeg", "png", "gif", "webp", "svg", "pdf", "zip", "rar", "exe", "txt", "csv"];

/* ---------------- Reusable fields ---------------- */
function Field({ label, value, onChange, placeholder, testid, textarea, rows = 3 }) {
  const cls =
    "mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-zinc-100 outline-none transition-colors focus:border-[#ff6b00]";
  return (
    <label className="block">
      <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</span>
      {textarea ? (
        <textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} />
      )}
    </label>
  );
}

function UploadButton({ onUploaded, label = "Subir archivo", accept, testid, maxMB = MAX_UPLOAD_MB }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Tipo no permitido: .${ext}. Permitidos: ${ALLOWED_EXT.join(", ")}`);
      if (ref.current) ref.current.value = "";
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`El archivo supera el máximo de ${maxMB} MB (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded({ ...data });
      toast.success("Archivo subido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo subir el archivo");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" />
      <button
        type="button"
        data-testid={testid}
        onClick={() => ref.current?.click()}
        className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-200 transition-colors hover:border-[#ff6b00]"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {label}
      </button>
    </>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0e0e11] p-6"
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-zinc-100">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={20} /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

/* ---------------- Admin page ---------------- */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  const { content, products, downloads, reload } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("general");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (content) {
      const d = structuredClone(content);
      if (!d.theme) d.theme = { ...DEFAULT_THEME };
      if (!d.products_section) d.products_section = { overline: "Equipos", title: "Elegí tu próxima herramienta.", subtitle: "" };
      if (!d.chat) d.chat = { title: "Asistente AUREX", welcome: "" };
      if (d.chat.status_label === undefined) d.chat.status_label = "En línea · IA";
      if (d.chat.input_placeholder === undefined) d.chat.input_placeholder = "Escribí tu consulta…";
      if (d.chat.error_message === undefined) d.chat.error_message = "Ups, no pude responder. Escribinos por WhatsApp y te ayudamos.";
      if (!d.seo) d.seo = { title: "", description: "", keywords: "", og_image: "", site_url: "" };
      if (d.seo.site_url === undefined) d.seo.site_url = "";
      if (!d.banner) d.banner = { enabled: false, message: "", link_text: "", link_target: "", dismissible: true };
      if (!d.footer) d.footer = { note: "", description: "", year: String(new Date().getFullYear()) };
      if (d.footer.description === undefined) d.footer.description = "";
      if (d.footer.year === undefined) d.footer.year = String(new Date().getFullYear());
      if (d.footer.nav_label === undefined) d.footer.nav_label = "Navegación";
      if (d.footer.follow_label === undefined) d.footer.follow_label = "Seguinos";
      if (d.footer.big_text === undefined) d.footer.big_text = d.brand?.name || "AUREX";
      if (d.footer.show_big_text === undefined) d.footer.show_big_text = true;
      if (d.brand && d.brand.favicon_url === undefined) d.brand.favicon_url = "";
      if (d.brand && d.brand.logo_url === undefined) d.brand.logo_url = "";
      if (d.brand && d.brand.logo_size === undefined) d.brand.logo_size = "medium";
      if (!d.nav) d.nav = { consult_label: "Consultar", admin_label: "Admin", mobile_cta: "Consultar por WhatsApp", show_admin_button: true, links: [
        { label: "Inicio", target: "#inicio" },
        { label: "Descargas", target: "#descargas" },
        { label: "Video", target: "#video" },
        { label: "Contacto", target: "#contacto" },
      ] };
      if (!d.contact_labels) d.contact_labels = { whatsapp: "WhatsApp", instagram: "Instagram", address: "Dirección", email: "Email" };
      if (d.hero && d.hero.side_card_overline === undefined) d.hero.side_card_overline = "Performance";
      if (d.hero && d.hero.side_card_title === undefined) d.hero.side_card_title = "RTX™ · DDR5 · NVMe";
      if (d.hero && d.hero.side_card_visible === undefined) d.hero.side_card_visible = true;
      if (d.hero && d.hero.scroll_label === undefined) d.hero.scroll_label = "Scroll";
      if (d.downloads_section && d.downloads_section.pending_label === undefined) d.downloads_section.pending_label = "Pronto";
      setDraft(d);
    }
  }, [content]);

  const set = (path, val) =>
    setDraft((prev) => {
      const copy = structuredClone(prev);
      const keys = path.split(".");
      let o = copy;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = val;
      return copy;
    });

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      await reload();
      toast.success("Contenido guardado");
    } catch (err) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08080a]">
        <Logo size={48} showText={false} />
      </div>
    );
  }

  const contentTab = ["general", "theme", "hero", "manifesto", "sections", "media"].includes(tab);

  return (
    <div className="min-h-screen bg-[#08080a] text-zinc-100">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Logo size={32} />
            <span className="hidden font-mono-jb text-[10px] uppercase tracking-[0.2em] text-zinc-600 sm:block">CMS</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} data-testid="admin-view-site" className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-zinc-300 transition-colors hover:border-white/30">
              <Home size={14} /> Ver sitio
            </button>
            {contentTab && (
              <button onClick={saveContent} disabled={saving} data-testid="admin-save" className="flex items-center gap-2 rounded-lg copper-bg px-4 py-2 text-xs font-semibold text-[#0a0a0c] transition-transform hover:scale-[1.03] disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
              </button>
            )}
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-zinc-400 transition-colors hover:border-white/30 hover:text-zinc-100">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-8">
        {/* Tabs */}
        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                tab === t.id ? "copper-bg text-[#0a0a0c]" : "border border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "general" && <GeneralTab draft={draft} set={set} />}
        {tab === "theme" && <ThemeTab draft={draft} set={set} />}
        {tab === "hero" && <HeroTab draft={draft} set={set} />}
        {tab === "manifesto" && <ManifestoTab draft={draft} set={set} />}
        {tab === "sections" && <SectionsTab draft={draft} set={set} />}
        {tab === "media" && <MediaTab draft={draft} set={set} />}
        {tab === "products" && <ProductsTab products={products} reload={reload} />}
        {tab === "downloads" && <DownloadsTab downloads={downloads} reload={reload} />}
        {tab === "files" && <FilesTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "account" && <AccountTab />}
      </div>
    </div>
  );
}

/* ---------------- Content tabs ---------------- */
function Card({ title, children }) {
  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
      {title && <h3 className="mb-5 font-display text-lg font-bold text-zinc-100">{title}</h3>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function GeneralTab({ draft, set }) {
  return (
    <>
      <Card title="Marca">
        <Field label="Nombre" value={draft.brand?.name} onChange={(v) => set("brand.name", v)} testid="f-brand-name" />
        <Field label="Eslogan" value={draft.brand?.tagline} onChange={(v) => set("brand.tagline", v)} testid="f-brand-tagline" />
        <div className="md:col-span-2">
          <Field label="Logo (URL de imagen — opcional, reemplaza el logo por defecto)" value={draft.brand?.logo_url} onChange={(v) => set("brand.logo_url", v)} testid="f-brand-logo" />
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <UploadButton accept="image/*" label="Subir logo" onUploaded={(d) => set("brand.logo_url", d.url)} testid="upload-logo" />
            {draft.brand?.logo_url ? (
              <>
                <div className="flex h-14 items-center rounded-lg border border-white/10 bg-black/40 px-3">
                  <img src={resolveMedia(draft.brand.logo_url)} alt="logo" className="h-10 w-auto object-contain" />
                </div>
                <button type="button" onClick={() => set("brand.logo_url", "")} data-testid="remove-logo" className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500 hover:text-red-400">
                  Quitar logo
                </button>
              </>
            ) : (
              <span className="font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-600">Usando logo por defecto (X AUREX)</span>
            )}
          </div>
          <div className="mt-4">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Tamaño del logo</span>
            <div className="mt-2 flex gap-2" data-testid="logo-size-group">
              {[
                { id: "small", label: "Pequeño" },
                { id: "medium", label: "Mediano" },
                { id: "large", label: "Grande" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => set("brand.logo_size", o.id)}
                  data-testid={`logo-size-${o.id}`}
                  className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                    (draft.brand?.logo_size || "medium") === o.id
                      ? "copper-bg text-[#0a0a0c]"
                      : "border border-white/10 text-zinc-400 hover:text-zinc-100"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <Field label="Favicon (ícono de pestaña — opcional)" value={draft.brand?.favicon_url} onChange={(v) => set("brand.favicon_url", v)} testid="f-brand-favicon" />
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <UploadButton accept="image/*" label="Subir favicon" onUploaded={(d) => set("brand.favicon_url", d.url)} testid="upload-favicon" />
            {draft.brand?.favicon_url ? (
              <>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-black/40 p-1.5">
                  <img src={resolveMedia(draft.brand.favicon_url)} alt="favicon" className="max-h-full max-w-full object-contain" />
                </div>
                <button type="button" onClick={() => set("brand.favicon_url", "")} data-testid="remove-favicon" className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500 hover:text-red-400">
                  Quitar favicon
                </button>
              </>
            ) : (
              <span className="font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-600">Usando favicon por defecto (X)</span>
            )}
          </div>
        </div>
      </Card>
      <Card title="Contacto">
        <Field label="WhatsApp (solo números)" value={draft.contact?.whatsapp} onChange={(v) => set("contact.whatsapp", v)} testid="f-wa" />
        <Field label="WhatsApp (para mostrar)" value={draft.contact?.whatsapp_display} onChange={(v) => set("contact.whatsapp_display", v)} testid="f-wa-display" />
        <Field label="Instagram (usuario)" value={draft.contact?.instagram} onChange={(v) => set("contact.instagram", v)} testid="f-ig" />
        <Field label="Instagram URL" value={draft.contact?.instagram_url} onChange={(v) => set("contact.instagram_url", v)} testid="f-ig-url" />
        <Field label="Dirección" value={draft.contact?.address} onChange={(v) => set("contact.address", v)} testid="f-address" />
        <Field label="Email" value={draft.contact?.email} onChange={(v) => set("contact.email", v)} testid="f-email" />
        <div className="md:col-span-2">
          <Field label="Mensaje predefinido de WhatsApp" value={draft.contact?.whatsapp_message} onChange={(v) => set("contact.whatsapp_message", v)} testid="f-wa-msg" />
        </div>
      </Card>
      <Card title="Modo Vacaciones (banner arriba de todo)">
        <div className="md:col-span-2">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={!!draft.banner?.enabled}
              onChange={(e) => set("banner.enabled", e.target.checked)}
              data-testid="f-banner-enabled"
              className="h-4 w-4 accent-[#ff6b00]"
            />
            <span className="text-sm text-zinc-300">Mostrar banner arriba del sitio</span>
          </label>
        </div>
        <div className="md:col-span-2">
          <Field label="Mensaje" textarea rows={2} value={draft.banner?.message} onChange={(v) => set("banner.message", v)} testid="f-banner-msg" placeholder="Estamos de vacaciones hasta el 5 de enero…" />
        </div>
        <Field label="Texto del link (opcional)" value={draft.banner?.link_text} onChange={(v) => set("banner.link_text", v)} testid="f-banner-link-text" placeholder="Ver detalle" />
        <Field label="URL o ancla del link (opcional)" value={draft.banner?.link_target} onChange={(v) => set("banner.link_target", v)} testid="f-banner-link-target" placeholder="#contacto o https://…" />
        <div className="md:col-span-2">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={draft.banner?.dismissible !== false}
              onChange={(e) => set("banner.dismissible", e.target.checked)}
              data-testid="f-banner-dismiss"
              className="h-4 w-4 accent-[#ff6b00]"
            />
            <span className="text-sm text-zinc-300">Permitir que el visitante cierre el aviso</span>
          </label>
        </div>
      </Card>
      <Card title="SEO (posicionamiento en Google)">
        <div className="md:col-span-2">
          <Field label="Sitio web (URL definitiva de tu dominio)" value={draft.seo?.site_url} onChange={(v) => set("seo.site_url", v)} testid="f-seo-siteurl" placeholder="https://aurex.com.ar" />
          <p className="mt-2 text-xs text-zinc-500">Cuando conectes tu dominio final, pegalo acá. Se usa para las URL canónicas y los enlaces al compartir.</p>
        </div>
        <div className="md:col-span-2">
          <Field label="Título SEO (pestaña / Google)" value={draft.seo?.title} onChange={(v) => set("seo.title", v)} testid="f-seo-title" placeholder="AUREX — Notebooks de alta gama" />
        </div>
        <div className="md:col-span-2">
          <Field label="Descripción SEO (meta description)" textarea rows={2} value={draft.seo?.description} onChange={(v) => set("seo.description", v)} testid="f-seo-desc" placeholder="Descripción que aparece en los resultados de búsqueda" />
        </div>
        <div className="md:col-span-2">
          <Field label="Palabras clave (separadas por coma)" value={draft.seo?.keywords} onChange={(v) => set("seo.keywords", v)} testid="f-seo-keywords" placeholder="notebooks, laptops, alta gama, AUREX" />
        </div>
        <div className="md:col-span-2">
          <Field label="Imagen para compartir (Open Graph)" value={draft.seo?.og_image} onChange={(v) => set("seo.og_image", v)} testid="f-seo-og" placeholder="URL de imagen (1200x630 recomendado)" />
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <UploadButton accept="image/*" label="Subir imagen OG" onUploaded={(d) => set("seo.og_image", d.url)} testid="upload-og" />
            {draft.seo?.og_image && <img src={resolveMedia(draft.seo.og_image)} alt="og" className="h-14 w-24 rounded-lg object-cover" />}
          </div>
        </div>
      </Card>
    </>
  );
}

function HeroTab({ draft, set }) {
  return (
    <>
      <Card title="Sección Inicio (Hero)">
        <Field label="Overline" value={draft.hero?.overline} onChange={(v) => set("hero.overline", v)} testid="f-hero-overline" />
        <Field label="Subtítulo" value={draft.hero?.subtitle} onChange={(v) => set("hero.subtitle", v)} testid="f-hero-subtitle" />
        <Field label="Título línea 1" value={draft.hero?.title_line_1} onChange={(v) => set("hero.title_line_1", v)} testid="f-hero-t1" />
        <Field label="Título línea 2" value={draft.hero?.title_line_2} onChange={(v) => set("hero.title_line_2", v)} testid="f-hero-t2" />
        <Field label="Botón primario" value={draft.hero?.cta_primary} onChange={(v) => set("hero.cta_primary", v)} testid="f-hero-cta1" />
        <Field label="Botón secundario" value={draft.hero?.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} testid="f-hero-cta2" />
        <Field label="Texto de scroll" value={draft.hero?.scroll_label} onChange={(v) => set("hero.scroll_label", v)} testid="f-hero-scroll" placeholder="Scroll" />
        <div className="md:col-span-2">
          <Field label="Imagen (URL)" value={draft.hero?.image} onChange={(v) => set("hero.image", v)} testid="f-hero-img" />
          <div className="mt-3 flex items-center gap-4">
            <UploadButton accept="image/*" label="Subir imagen" onUploaded={(d) => set("hero.image", d.url)} testid="upload-hero" />
            {draft.hero?.image && <img src={resolveMedia(draft.hero.image)} alt="" className="h-16 w-28 rounded-lg object-cover" />}
          </div>
        </div>
      </Card>
      <Card title="Tarjeta flotante sobre la imagen del hero">
        <Field label="Etiqueta (overline)" value={draft.hero?.side_card_overline} onChange={(v) => set("hero.side_card_overline", v)} testid="f-hero-sc-overline" placeholder="Performance" />
        <Field label="Título grande" value={draft.hero?.side_card_title} onChange={(v) => set("hero.side_card_title", v)} testid="f-hero-sc-title" placeholder="RTX™ · DDR5 · NVMe" />
        <div className="md:col-span-2">
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={draft.hero?.side_card_visible !== false} onChange={(e) => set("hero.side_card_visible", e.target.checked)} data-testid="f-hero-sc-visible" className="h-4 w-4 accent-[#ff6b00]" />
            <span className="text-sm text-zinc-300">Mostrar tarjeta flotante en el hero</span>
          </label>
        </div>
      </Card>
    </>
  );
}

function ManifestoTab({ draft, set }) {
  const chapters = draft.manifesto?.chapters || [];
  return (
    <>
      <Card title="Manifiesto">
        <div className="md:col-span-2">
          <Field label="Overline" value={draft.manifesto?.overline} onChange={(v) => set("manifesto.overline", v)} testid="f-man-overline" />
        </div>
        <div className="md:col-span-2">
          <Field label="Título" textarea value={draft.manifesto?.title} onChange={(v) => set("manifesto.title", v)} testid="f-man-title" />
        </div>
      </Card>
      {chapters.map((c, i) => (
        <div key={i} className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
          <div className="mb-3 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#ff8c3a]">Capítulo {c.number}</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Field label="N°" value={c.number} onChange={(v) => set(`manifesto.chapters.${i}.number`, v)} testid={`f-chap-num-${i}`} />
            <div className="md:col-span-3">
              <Field label="Título" value={c.title} onChange={(v) => set(`manifesto.chapters.${i}.title`, v)} testid={`f-chap-title-${i}`} />
            </div>
            <div className="md:col-span-4">
              <Field label="Texto" textarea value={c.text} onChange={(v) => set(`manifesto.chapters.${i}.text`, v)} testid={`f-chap-text-${i}`} />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function MediaTab({ draft, set }) {
  return (
    <Card title="Sección Video">
      <Field label="Overline" value={draft.video?.overline} onChange={(v) => set("video.overline", v)} testid="f-vid-overline" />
      <Field label="Título" value={draft.video?.title} onChange={(v) => set("video.title", v)} testid="f-vid-title" />
      <div className="md:col-span-2">
        <Field label="Subtítulo" value={draft.video?.subtitle} onChange={(v) => set("video.subtitle", v)} testid="f-vid-subtitle" />
      </div>
      <Field label="URL de YouTube" value={draft.video?.youtube_url} onChange={(v) => set("video.youtube_url", v)} testid="f-vid-url" />
      <div>
        <Field label="Poster (URL)" value={draft.video?.poster} onChange={(v) => set("video.poster", v)} testid="f-vid-poster" />
        <div className="mt-3">
          <UploadButton accept="image/*" label="Subir poster" onUploaded={(d) => set("video.poster", d.url)} testid="upload-poster" />
        </div>
      </div>
    </Card>
  );
}

/* ---------------- Products ---------------- */
const emptyProduct = { name: "", tagline: "", description: "", image: "", price: "Consultar", badge: "", specs: [] };

function ProductsTab({ products, reload }) {
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [localProducts, setLocalProducts] = useState(products);

  useEffect(() => { setLocalProducts(products); }, [products]);

  const openNew = () => { setEditing(structuredClone(emptyProduct)); setIsNew(true); };
  const openEdit = (p) => { setEditing(structuredClone(p)); setIsNew(false); };

  const save = async () => {
    try {
      if (isNew) await api.post("/admin/products", editing);
      else await api.put(`/admin/products/${editing.id}`, editing);
      await reload();
      setEditing(null);
      toast.success("Producto guardado");
    } catch { toast.error("No se pudo guardar el producto"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try { await api.delete(`/admin/products/${id}`); await reload(); toast.success("Producto eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setSpec = (i, k, v) => setEditing((e) => { const s = [...e.specs]; s[i] = { ...s[i], [k]: v }; return { ...e, specs: s }; });

  const persistOrder = async (newList) => {
    setLocalProducts(newList);
    try {
      await api.post("/admin/products/reorder", { items: newList.map((p) => ({ id: p.id })) });
      await reload();
    } catch {
      toast.error("No se pudo guardar el orden");
      await reload();
    }
  };

  const onReorder = (newIds) => {
    const map = Object.fromEntries(localProducts.map((p) => [p.id, p]));
    const newList = newIds.map((id) => map[id]).filter(Boolean);
    persistOrder(newList);
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold">Productos ({localProducts.length})</h3>
        <button onClick={openNew} data-testid="add-product" className="flex items-center gap-2 rounded-lg copper-bg px-4 py-2 text-xs font-semibold text-[#0a0a0c]"><Plus size={14} /> Agregar</button>
      </div>
      <p className="mb-3 font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">
        Arrastrá para reordenar los productos en el sitio
      </p>
      <SortableList ids={localProducts.map((p) => p.id)} onReorder={onReorder}>
        <div className="grid grid-cols-1 gap-3">
          {localProducts.map((p) => (
            <SortableRow key={p.id} id={p.id}>
              <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0e0e11] p-4" data-testid={`admin-product-${p.id}`}>
                {p.image && <img src={resolveMedia(p.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display font-bold text-zinc-100">{p.name}</div>
                  <div className="truncate text-xs text-zinc-500">{p.tagline}</div>
                </div>
                <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-zinc-100"><Pencil size={16} /></button>
                <button onClick={() => remove(p.id)} data-testid={`del-product-${p.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </SortableRow>
          ))}
        </div>
      </SortableList>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={isNew ? "Nuevo producto" : "Editar producto"}>
        {editing && (
          <div className="space-y-4">
            <Field label="Nombre" value={editing.name} onChange={(v) => setF("name", v)} testid="p-name" />
            <Field label="Bajada" value={editing.tagline} onChange={(v) => setF("tagline", v)} testid="p-tagline" />
            <Field label="Descripción" textarea value={editing.description} onChange={(v) => setF("description", v)} testid="p-desc" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Precio" value={editing.price} onChange={(v) => setF("price", v)} testid="p-price" />
              <Field label="Etiqueta" value={editing.badge} onChange={(v) => setF("badge", v)} testid="p-badge" />
            </div>
            <div>
              <Field label="Imagen (URL)" value={editing.image} onChange={(v) => setF("image", v)} testid="p-img" />
              <div className="mt-2"><UploadButton accept="image/*" label="Subir imagen" onUploaded={(d) => setF("image", d.url)} testid="p-upload" /></div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Especificaciones</span>
                <button onClick={() => setF("specs", [...(editing.specs || []), { label: "", value: "" }])} className="text-xs text-[#ff8c3a]">+ Agregar</button>
              </div>
              <div className="space-y-2">
                {(editing.specs || []).map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} placeholder="Etiqueta" className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
                    <input value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="Valor" className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
                    <button onClick={() => setF("specs", editing.specs.filter((_, k) => k !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={save} data-testid="p-save" className="w-full rounded-xl copper-bg py-3 text-sm font-semibold text-[#0a0a0c]">Guardar producto</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Downloads ---------------- */
const DL_CATEGORIES = ["Video", "LAN", "WiFi", "Bluetooth", "BIOS", "Chipset", "Audio", "Fichas técnicas", "Manuales", "Drivers", "Catálogo"];
const emptyDownload = { title: "", description: "", category: "Video", file_type: "", file_url: "", size: "—", version: "" };

function BulkDownloadUpload({ reload }) {
  const ref = useRef(null);
  const [category, setCategory] = useState("Video");
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category.trim());
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/downloads/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      const nSkip = (data.skipped || []).length;
      toast.success(`${data.created.length} archivo(s) subido(s) en "${category}"${nSkip ? `, ${nSkip} omitido(s)` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron subir");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="mb-6 rounded-2xl border border-[#ff6b00]/30 bg-white/[0.02] p-5">
      <h4 className="font-display text-base font-bold text-zinc-100">Carga múltiple (drivers)</h4>
      <p className="mt-1 text-xs text-zinc-500">Elegí una categoría (Video, LAN, WiFi, Bluetooth, BIOS…) y subí varios archivos de una vez.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Categoría</span>
          <input list="dl-cats" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="bulk-category" className="mt-2 w-56 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
        </label>
        <input ref={ref} type="file" multiple onChange={handle} className="hidden" />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy || !category.trim()} data-testid="bulk-upload-btn" className="flex items-center gap-2 rounded-xl copper-bg px-5 py-2.5 text-sm font-semibold text-[#0a0a0c] transition-transform hover:scale-[1.02] disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Subir varios archivos
        </button>
      </div>
    </div>
  );
}

function DownloadsTab({ downloads, reload }) {
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [localDownloads, setLocalDownloads] = useState(downloads);

  useEffect(() => { setLocalDownloads(downloads); }, [downloads]);

  const openNew = () => { setEditing(structuredClone(emptyDownload)); setIsNew(true); };
  const openEdit = (d) => { setEditing(structuredClone(d)); setIsNew(false); };
  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    try {
      if (isNew) await api.post("/admin/downloads", editing);
      else await api.put(`/admin/downloads/${editing.id}`, editing);
      await reload();
      setEditing(null);
      toast.success("Descarga guardada");
    } catch { toast.error("No se pudo guardar"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta descarga?")) return;
    try { await api.delete(`/admin/downloads/${id}`); await reload(); toast.success("Eliminada"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const removeCategory = async (category, count) => {
    if (!window.confirm(`¿Eliminar TODAS las descargas de "${category}" (${count})? Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.delete(`/admin/downloads/category/${encodeURIComponent(category)}`);
      await reload();
      toast.success(`${data.deleted} descarga(s) eliminada(s) de "${category}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar la categoría");
    }
  };

  const onFile = (d) => {
    const ext = (d.filename?.split(".").pop() || "").toUpperCase();
    setEditing((e) => ({ ...e, file_url: d.url, size: d.size, file_type: ext || e.file_type }));
    toast.success("Archivo listo");
  };

  // group admin list by category (preserve current order)
  const groups = [];
  const gidx = {};
  localDownloads.forEach((d) => {
    const c = d.category || "Otros";
    if (gidx[c] === undefined) { gidx[c] = groups.length; groups.push({ category: c, items: [] }); }
    groups[gidx[c]].items.push(d);
  });

  const persistOrder = async (flat) => {
    setLocalDownloads(flat);
    try {
      await api.post("/admin/downloads/reorder", {
        items: flat.map((d) => ({ id: d.id })),
      });
      await reload();
    } catch {
      toast.error("No se pudo guardar el orden");
      await reload();
    }
  };

  const reorderGroups = (newCategoryOrder) => {
    const gmap = Object.fromEntries(groups.map((g) => [g.category, g.items]));
    const flat = newCategoryOrder.flatMap((cat) => gmap[cat] || []);
    persistOrder(flat);
  };

  const reorderItemsInGroup = (category, newIds) => {
    const gmap = Object.fromEntries(groups.map((g) => [g.category, g.items]));
    const idToItem = Object.fromEntries((gmap[category] || []).map((it) => [it.id, it]));
    const newItemsForCat = newIds.map((id) => idToItem[id]).filter(Boolean);
    const flat = groups.flatMap((g) => (g.category === category ? newItemsForCat : g.items));
    persistOrder(flat);
  };

  return (
    <div>
      <datalist id="dl-cats">
        {DL_CATEGORIES.map((c) => <option key={c} value={c} />)}
      </datalist>

      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold">Descargas ({localDownloads.length})</h3>
        <button onClick={openNew} data-testid="add-download" className="flex items-center gap-2 rounded-lg copper-bg px-4 py-2 text-xs font-semibold text-[#0a0a0c]"><Plus size={14} /> Agregar uno</button>
      </div>

      <BulkDownloadUpload reload={reload} />

      <p className="mb-3 font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">
        Arrastrá para reordenar grupos y archivos
      </p>

      <SortableList
        ids={groups.map((g) => g.category)}
        onReorder={reorderGroups}
      >
        <div className="space-y-4">
          {groups.map((group) => (
            <SortableRow key={group.category} id={group.category}>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-mono-jb text-[10px] uppercase tracking-[0.18em] text-[#ff8c3a]">{group.category}</span>
                  <span className="font-mono-jb text-[10px] text-zinc-600">({group.items.length})</span>
                  <button
                    onClick={() => removeCategory(group.category, group.items.length)}
                    data-testid={`del-category-${group.category}`}
                    title={`Eliminar toda la categoría "${group.category}"`}
                    className="ml-auto flex items-center gap-1 rounded-lg border border-white/10 px-2 py-1 font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-500 transition-colors hover:border-red-500/40 hover:text-red-400"
                  >
                    <Trash2 size={12} /> Vaciar
                  </button>
                </div>
                <SortableList
                  ids={group.items.map((d) => d.id)}
                  onReorder={(ids) => reorderItemsInGroup(group.category, ids)}
                >
                  <div className="space-y-2">
                    {group.items.map((d) => (
                      <SortableRow key={d.id} id={d.id}>
                        <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0e0e11] p-4" data-testid={`admin-download-${d.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-display font-bold text-zinc-100">{d.title}</div>
                            <div className="truncate font-mono-jb text-[11px] text-zinc-500">{d.file_type || "—"} · {d.size} · {d.file_url ? "cargado" : "sin archivo"}</div>
                          </div>
                          {d.file_url && <a href={resolveMedia(d.file_url)} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-[#ff8c3a]"><ExternalLink size={16} /></a>}
                          <button onClick={() => openEdit(d)} className="text-zinc-400 hover:text-zinc-100"><Pencil size={16} /></button>
                          <button onClick={() => remove(d.id)} data-testid={`del-download-${d.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>
                        </div>
                      </SortableRow>
                    ))}
                  </div>
                </SortableList>
              </div>
            </SortableRow>
          ))}
        </div>
      </SortableList>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={isNew ? "Nueva descarga" : "Editar descarga"}>
        {editing && (
          <div className="space-y-4">
            <Field label="Título" value={editing.title} onChange={(v) => setF("title", v)} testid="d-title" />
            <Field label="Descripción" textarea value={editing.description} onChange={(v) => setF("description", v)} testid="d-desc" />
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Categoría</span>
                <input list="dl-cats" value={editing.category} onChange={(e) => setF("category", e.target.value)} data-testid="d-cat" placeholder="Video, LAN, WiFi, BIOS…" className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
              </label>
              <Field label="Versión" value={editing.version} onChange={(v) => setF("version", v)} testid="d-ver" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tipo (PDF/ZIP...)" value={editing.file_type} onChange={(v) => setF("file_type", v)} testid="d-type" />
              <Field label="Tamaño" value={editing.size} onChange={(v) => setF("size", v)} testid="d-size" />
            </div>
            <div>
              <Field label="URL del archivo" value={editing.file_url} onChange={(v) => setF("file_url", v)} testid="d-url" />
              <div className="mt-2"><UploadButton label="Subir archivo real" onUploaded={onFile} testid="d-upload" /></div>
            </div>
            <button onClick={save} data-testid="d-save" className="w-full rounded-xl copper-bg py-3 text-sm font-semibold text-[#0a0a0c]">Guardar descarga</button>
          </div>
        )}
      </Modal>
    </div>
  );
}


/* ---------------- String list editor ---------------- */
function StringList({ label, items = [], onChange, placeholder = "Texto" }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</span>
        <button type="button" onClick={() => onChange([...items, ""])} className="text-xs text-[#ff8c3a]">+ Agregar</button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={it}
              onChange={(e) => { const c = [...items]; c[i] = e.target.value; onChange(c); }}
              placeholder={placeholder}
              className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]"
            />
            <button type="button" onClick={() => onChange(items.filter((_, k) => k !== i))} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Secciones tab (todo editable) ---------------- */
function SectionsTab({ draft, set }) {
  const showcaseItems = draft.showcase?.items || [];
  const featureGroups = draft.features?.groups || [];
  const navLinks = draft.nav?.links || [];

  const addShowcase = () => set("showcase.items", [...showcaseItems, { title: "", text: "", image: "", features: [] }]);
  const addGroup = () => set("features.groups", [...featureGroups, { title: "", items: [] }]);
  const addNavLink = () => set("nav.links", [...navLinks, { label: "", target: "#" }]);

  return (
    <>
      {/* Navegación editable */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-zinc-100">Navegación (barra superior)</h3>
          <button onClick={addNavLink} className="text-xs text-[#ff8c3a]" data-testid="add-nav-link">+ Agregar link</button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Botón CTA (desktop)" value={draft.nav?.consult_label} onChange={(v) => set("nav.consult_label", v)} testid="f-nav-cta" placeholder="Consultar" />
          <Field label="Botón CTA (móvil)" value={draft.nav?.mobile_cta} onChange={(v) => set("nav.mobile_cta", v)} testid="f-nav-mcta" placeholder="Consultar por WhatsApp" />
          <Field label="Texto botón Admin" value={draft.nav?.admin_label} onChange={(v) => set("nav.admin_label", v)} testid="f-nav-admin" placeholder="Admin" />
        </div>
        <div className="mt-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={draft.nav?.show_admin_button !== false}
              onChange={(e) => set("nav.show_admin_button", e.target.checked)}
              data-testid="f-nav-show-admin"
              className="h-4 w-4 accent-[#ff6b00]"
            />
            <span className="text-sm text-zinc-300">Mostrar botón &quot;Admin&quot; en la barra pública (si lo apagás, ingresás por /login)</span>
          </label>
        </div>
        <div className="mt-5">
          <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Links del menú</span>
          <div className="mt-2 space-y-2">
            {navLinks.map((l, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3 md:grid-cols-[1fr_1fr_auto]">
                <input value={l.label || ""} onChange={(e) => set(`nav.links.${i}.label`, e.target.value)} placeholder="Etiqueta (ej: Inicio)" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
                <input value={l.target || ""} onChange={(e) => set(`nav.links.${i}.target`, e.target.value)} placeholder="Ancla o URL (ej: #inicio o https://…)" className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#ff6b00]" />
                <button onClick={() => set("nav.links", navLinks.filter((_, k) => k !== i))} data-testid={`nav-link-del-${i}`} className="rounded-lg border border-white/10 px-3 text-zinc-500 hover:border-red-500/40 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Showcase */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-zinc-100">Showcase (Detalles)</h3>
          <button onClick={addShowcase} className="text-xs text-[#ff8c3a]" data-testid="add-showcase">+ Agregar bloque</button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Overline" value={draft.showcase?.overline} onChange={(v) => set("showcase.overline", v)} testid="f-sc-overline" />
          <Field label="Título" value={draft.showcase?.title} onChange={(v) => set("showcase.title", v)} testid="f-sc-title" />
        </div>
        <div className="mt-4 space-y-4">
          {showcaseItems.map((it, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-[#ff8c3a]">Bloque {i + 1}</span>
                <button onClick={() => set("showcase.items", showcaseItems.filter((_, k) => k !== i))} className="text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Título" value={it.title} onChange={(v) => set(`showcase.items.${i}.title`, v)} />
                <Field label="Texto" value={it.text} onChange={(v) => set(`showcase.items.${i}.text`, v)} />
              </div>
              <div className="mt-3">
                <Field label="Imagen (URL)" value={it.image} onChange={(v) => set(`showcase.items.${i}.image`, v)} />
                <div className="mt-2 flex items-center gap-3">
                  <UploadButton accept="image/*" label="Subir imagen" onUploaded={(d) => set(`showcase.items.${i}.image`, d.url)} />
                  {it.image && <img src={resolveMedia(it.image)} alt="" className="h-12 w-20 rounded object-cover" />}
                </div>
              </div>
              <div className="mt-3">
                <StringList label="Features" items={it.features || []} onChange={(v) => set(`showcase.items.${i}.features`, v)} placeholder="Ej: Cuerpo de aluminio" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-zinc-100">Especificaciones</h3>
          <button onClick={addGroup} className="text-xs text-[#ff8c3a]" data-testid="add-feature-group">+ Agregar grupo</button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Overline" value={draft.features?.overline} onChange={(v) => set("features.overline", v)} testid="f-ft-overline" />
          <Field label="Título" value={draft.features?.title} onChange={(v) => set("features.title", v)} testid="f-ft-title" />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {featureGroups.map((g, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 flex items-center justify-between">
                <Field label="Grupo" value={g.title} onChange={(v) => set(`features.groups.${i}.title`, v)} />
                <button onClick={() => set("features.groups", featureGroups.filter((_, k) => k !== i))} className="ml-2 mt-6 text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
              <div className="mt-2">
                <StringList label="Items" items={g.items || []} onChange={(v) => set(`features.groups.${i}.items`, v)} placeholder="Ej: Wi-Fi 6E" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-zinc-100">Marquee (texto animado)</h3>
        <StringList label="Palabras" items={draft.marquee?.items || []} onChange={(v) => set("marquee.items", v)} placeholder="Ej: POTENCIA" />
      </div>

      {/* Section headers + footer */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-zinc-100">Encabezados de secciones</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Productos · Overline" value={draft.products_section?.overline} onChange={(v) => set("products_section.overline", v)} testid="f-ps-overline" />
          <Field label="Productos · Título" value={draft.products_section?.title} onChange={(v) => set("products_section.title", v)} testid="f-ps-title" />
          <Field label="Productos · Subtítulo" value={draft.products_section?.subtitle} onChange={(v) => set("products_section.subtitle", v)} testid="f-ps-subtitle" />
          <Field label="Descargas · Overline" value={draft.downloads_section?.overline} onChange={(v) => set("downloads_section.overline", v)} />
          <Field label="Descargas · Título" value={draft.downloads_section?.title} onChange={(v) => set("downloads_section.title", v)} />
          <Field label="Descargas · Subtítulo" value={draft.downloads_section?.subtitle} onChange={(v) => set("downloads_section.subtitle", v)} />
          <Field label="Descargas · Etiqueta 'Pronto'" value={draft.downloads_section?.pending_label} onChange={(v) => set("downloads_section.pending_label", v)} testid="f-ds-pending" placeholder="Pronto" />
          <Field label="Contacto · Overline" value={draft.contact_section?.overline} onChange={(v) => set("contact_section.overline", v)} />
          <Field label="Contacto · Título" value={draft.contact_section?.title} onChange={(v) => set("contact_section.title", v)} />
          <Field label="Contacto · Subtítulo" value={draft.contact_section?.subtitle} onChange={(v) => set("contact_section.subtitle", v)} />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <Field label="Contacto · Etiqueta WhatsApp" value={draft.contact_labels?.whatsapp} onChange={(v) => set("contact_labels.whatsapp", v)} testid="f-cl-wa" placeholder="WhatsApp" />
          <Field label="Contacto · Etiqueta Instagram" value={draft.contact_labels?.instagram} onChange={(v) => set("contact_labels.instagram", v)} testid="f-cl-ig" placeholder="Instagram" />
          <Field label="Contacto · Etiqueta Dirección" value={draft.contact_labels?.address} onChange={(v) => set("contact_labels.address", v)} testid="f-cl-addr" placeholder="Dirección" />
          <Field label="Contacto · Etiqueta Email" value={draft.contact_labels?.email} onChange={(v) => set("contact_labels.email", v)} testid="f-cl-email" placeholder="Email" />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Footer · Año" value={draft.footer?.year} onChange={(v) => set("footer.year", v)} testid="f-footer-year" placeholder={String(new Date().getFullYear())} />
          <div className="md:col-span-2">
            <Field label="Footer · Nota (© {año} + este texto)" value={draft.footer?.note} onChange={(v) => set("footer.note", v)} testid="f-footer-note" />
          </div>
          <div className="md:col-span-3">
            <Field label="Footer · Descripción" value={draft.footer?.description} onChange={(v) => set("footer.description", v)} testid="f-footer-desc" />
          </div>
          <Field label="Footer · Etiqueta Navegación" value={draft.footer?.nav_label} onChange={(v) => set("footer.nav_label", v)} testid="f-footer-nav" placeholder="Navegación" />
          <Field label="Footer · Etiqueta Seguinos" value={draft.footer?.follow_label} onChange={(v) => set("footer.follow_label", v)} testid="f-footer-follow" placeholder="Seguinos" />
          <Field label="Footer · Texto gigante" value={draft.footer?.big_text} onChange={(v) => set("footer.big_text", v)} testid="f-footer-big" placeholder="AUREX" />
          <div className="md:col-span-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={draft.footer?.show_big_text !== false}
                onChange={(e) => set("footer.show_big_text", e.target.checked)}
                data-testid="f-footer-show-big"
                className="h-4 w-4 accent-[#ff6b00]"
              />
              <span className="text-sm text-zinc-300">Mostrar texto gigante decorativo abajo del footer</span>
            </label>
          </div>
        </div>
      </div>

      {/* Chat assistant */}
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h3 className="mb-5 font-display text-lg font-bold text-zinc-100">Asistente (chat IA)</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Título del chat" value={draft.chat?.title} onChange={(v) => set("chat.title", v)} testid="f-chat-title" />
          <Field label="Etiqueta de estado" value={draft.chat?.status_label} onChange={(v) => set("chat.status_label", v)} testid="f-chat-status" placeholder="En línea · IA" />
          <Field label="Mensaje de bienvenida" value={draft.chat?.welcome} onChange={(v) => set("chat.welcome", v)} testid="f-chat-welcome" />
          <Field label="Placeholder del input" value={draft.chat?.input_placeholder} onChange={(v) => set("chat.input_placeholder", v)} testid="f-chat-placeholder" placeholder="Escribí tu consulta…" />
          <div className="md:col-span-2">
            <Field label="Mensaje si falla el chat" textarea rows={2} value={draft.chat?.error_message} onChange={(v) => set("chat.error_message", v)} testid="f-chat-error" placeholder="Ups, no pude responder. Escribinos por WhatsApp y te ayudamos." />
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- Medios (file manager) ---------------- */
function FilesTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/files");
      setFiles(data);
    } catch {
      toast.error("No se pudieron cargar los archivos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este archivo? Dejará de mostrarse en el sitio.")) return;
    try { await api.delete(`/admin/files/${id}`); setFiles((f) => f.filter((x) => x.id !== id)); toast.success("Archivo eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const copy = (url) => {
    const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
    navigator.clipboard?.writeText(full);
    toast.success("URL copiada");
  };

  const isImage = (ct) => (ct || "").startsWith("image/");
  const isVideo = (ct) => (ct || "").startsWith("video/");

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold">Medios ({files.length})</h3>
        <div className="flex items-center gap-3">
          <span className="hidden font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-600 sm:block">Máx {MAX_UPLOAD_MB}MB</span>
          <UploadButton label="Subir archivo" onUploaded={load} testid="files-upload" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-600" /></div>
      ) : files.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center text-sm text-zinc-500">
          Todavía no subiste archivos. Subí fotos de las notebooks o archivos de descargas.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {files.map((f) => {
            const src = resolveMedia(f.url);
            return (
              <div key={f.id} className="group overflow-hidden rounded-xl border border-white/10 bg-[#0e0e11]" data-testid={`media-${f.id}`}>
                <div className="relative flex h-32 items-center justify-center bg-black/40">
                  {isImage(f.content_type) ? (
                    <img src={src} alt={f.original_filename} className="h-full w-full object-cover" />
                  ) : isVideo(f.content_type) ? (
                    <Film size={30} className="text-zinc-500" />
                  ) : (
                    <FileText size={30} className="text-zinc-500" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 opacity-0 transition-opacity group-hover:opacity-100">
                    <button onClick={() => copy(f.url)} title="Copiar URL" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-zinc-100 hover:bg-white/20"><Copy size={15} /></button>
                    <a href={src} target="_blank" rel="noreferrer" title="Abrir" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-zinc-100 hover:bg-white/20"><ExternalLink size={15} /></a>
                    <button onClick={() => remove(f.id)} data-testid={`media-del-${f.id}`} title="Eliminar" className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/80 text-white hover:bg-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="p-3">
                  <div className="truncate text-xs font-medium text-zinc-200">{f.original_filename}</div>
                  <div className="mt-0.5 font-mono-jb text-[10px] text-zinc-600">{f.size}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ---------------- Colores (theme editor) ---------------- */
const ACCENT_PRESETS = [
  { name: "Cobre", accent: "#ff6b00", accent_soft: "#ff8c3a", accent_2: "#ff3d00" },
  { name: "Ámbar", accent: "#f59e0b", accent_soft: "#fbbf24", accent_2: "#d97706" },
  { name: "Azul", accent: "#3b82f6", accent_soft: "#60a5fa", accent_2: "#2563eb" },
  { name: "Cian", accent: "#06b6d4", accent_soft: "#22d3ee", accent_2: "#0891b2" },
  { name: "Verde", accent: "#22c55e", accent_soft: "#4ade80", accent_2: "#16a34a" },
  { name: "Violeta", accent: "#8b5cf6", accent_soft: "#a78bfa", accent_2: "#7c3aed" },
  { name: "Rosa", accent: "#ec4899", accent_soft: "#f472b6", accent_2: "#db2777" },
  { name: "Rojo", accent: "#ef4444", accent_soft: "#f87171", accent_2: "#dc2626" },
];
const BG_PRESETS = [
  { name: "Negro", bg: "#08080a", surface: "#0e0e11", surface_2: "#0b0b0d", text: "#f4f4f5", muted: "#9a9aa0", on_accent: "#0a0a0c" },
  { name: "Grafito", bg: "#111318", surface: "#191c22", surface_2: "#14161b", text: "#eef0f3", muted: "#a1a6b0", on_accent: "#0a0a0c" },
  { name: "Azul noche", bg: "#0a0e1a", surface: "#101728", surface_2: "#0c1220", text: "#eef2fb", muted: "#94a0bd", on_accent: "#0a0a0c" },
  { name: "Claro", bg: "#f5f5f4", surface: "#ffffff", surface_2: "#ececea", text: "#18181b", muted: "#52525b", on_accent: "#ffffff" },
];

function ColorRow({ label, name, draft, set }) {
  const val = draft.theme?.[name] || "#000000";
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <input
        type="color"
        value={val}
        onChange={(e) => set(`theme.${name}`, e.target.value)}
        data-testid={`color-${name}`}
        className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-transparent"
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
        <input
          value={val}
          onChange={(e) => set(`theme.${name}`, e.target.value)}
          className="mt-0.5 w-full bg-transparent text-sm text-zinc-100 outline-none"
        />
      </div>
    </div>
  );
}

function ThemeTab({ draft, set }) {
  const { content } = useSite();
  const contentRef = useRef(content);
  useEffect(() => { contentRef.current = content; }, [content]);

  // live preview while editing
  useEffect(() => { applyTheme(draft.theme); }, [draft.theme]);
  // revert to the currently saved theme if leaving without saving
  useEffect(() => () => { applyTheme(contentRef.current?.theme || DEFAULT_THEME); }, []);

  const applyAccent = (p) => {
    set("theme.accent", p.accent);
    set("theme.accent_soft", p.accent_soft);
    set("theme.accent_2", p.accent_2);
  };
  const applyBg = (p) => {
    ["bg", "surface", "surface_2", "text", "muted", "on_accent"].forEach((k) => set(`theme.${k}`, p[k]));
  };
  const resetAll = () => Object.entries(DEFAULT_THEME).forEach(([k, v]) => set(`theme.${k}`, v));

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[color:var(--aurex-accent)]/30 bg-white/[0.02] p-4 text-sm text-zinc-400">
        Elegí los colores de todo el sitio. Los cambios se previsualizan al instante — tocá <span className="text-zinc-100 font-semibold">Guardar</span> (arriba) para publicarlos.
      </div>

      {/* Accent presets */}
      <div className="rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-zinc-100">Color de acento</h3>
          <button onClick={resetAll} data-testid="theme-reset" className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500 hover:text-zinc-200">Restablecer</button>
        </div>
        <div className="mb-5 flex flex-wrap gap-2">
          {ACCENT_PRESETS.map((p) => (
            <button
              key={p.name}
              onClick={() => applyAccent(p)}
              data-testid={`preset-accent-${p.name}`}
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-white/30"
            >
              <span className="h-4 w-4 rounded-full" style={{ background: `linear-gradient(135deg, ${p.accent}, ${p.accent_2})` }} />
              {p.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ColorRow label="Acento" name="accent" draft={draft} set={set} />
          <ColorRow label="Acento suave" name="accent_soft" draft={draft} set={set} />
          <ColorRow label="Acento 2 (degradé)" name="accent_2" draft={draft} set={set} />
          <ColorRow label="Texto sobre acento" name="on_accent" draft={draft} set={set} />
        </div>
      </div>

      {/* Background presets */}
      <div className="rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h3 className="mb-4 font-display text-lg font-bold text-zinc-100">Fondo y texto</h3>
        <div className="mb-5 flex flex-wrap gap-2">
          {BG_PRESETS.map((p) => (
            <button
              key={p.name}
              onClick={() => applyBg(p)}
              data-testid={`preset-bg-${p.name}`}
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-white/30"
            >
              <span className="h-4 w-4 rounded-full border border-white/20" style={{ background: p.bg }} />
              {p.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ColorRow label="Fondo" name="bg" draft={draft} set={set} />
          <ColorRow label="Superficie (tarjetas)" name="surface" draft={draft} set={set} />
          <ColorRow label="Superficie 2 (secciones)" name="surface_2" draft={draft} set={set} />
          <ColorRow label="Texto" name="text" draft={draft} set={set} />
          <ColorRow label="Texto atenuado" name="muted" draft={draft} set={set} />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Cuenta (cambiar usuario y clave) ---------------- */
function AccountTab() {
  const { user, updateAuth } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Ingresá tu contraseña actual para confirmar");
      return;
    }
    if (newPassword && newPassword !== confirm) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    const emailChanged = email && email.trim().toLowerCase() !== (user?.email || "");
    if (!newPassword && !emailChanged) {
      toast.error("No hay cambios para guardar");
      return;
    }
    setSaving(true);
    try {
      const body = { current_password: currentPassword };
      if (emailChanged) body.new_email = email.trim();
      if (newPassword) body.new_password = newPassword;
      const { data } = await api.post("/auth/change-credentials", body);
      updateAuth(data.token, data.user);
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      toast.success("Credenciales actualizadas correctamente");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-zinc-100 outline-none transition-colors focus:border-[#ff6b00]";

  return (
    <form onSubmit={submit} className="max-w-xl">
      <div className="mb-4 rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h3 className="mb-1 font-display text-lg font-bold text-zinc-100">Datos de acceso</h3>
        <p className="mb-5 text-sm text-zinc-500">Cambiá tu usuario (email) y/o contraseña. Los cambios quedan guardados y se mantienen al reiniciar.</p>

        <label className="block">
          <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Usuario (email)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="acc-email" />
        </label>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Nueva contraseña</span>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Dejar vacío para no cambiar" className={inputCls} data-testid="acc-newpass" />
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Repetir nueva contraseña</span>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="acc-confirm" />
          </label>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-[#ff6b00]/30 bg-white/[0.02] p-6">
        <label className="block">
          <span className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">Contraseña actual (requerida para confirmar)</span>
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputCls} data-testid="acc-current" required />
        </label>
      </div>

      <button type="submit" disabled={saving} data-testid="acc-save" className="flex items-center gap-2 rounded-xl copper-bg px-6 py-3 text-sm font-semibold text-[#0a0a0c] transition-transform hover:scale-[1.02] disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar cambios
      </button>
    </form>
  );
}



/* ---------------- Analítica (WhatsApp click tracking) ---------------- */
const SOURCE_LABEL = {
  "nav": "Nav (Consultar)",
  "nav-mobile": "Nav móvil",
  "floating": "Botón flotante",
  "contact-cta": "Contacto (botón grande)",
  "contact-card": "Contacto (tarjeta)",
  "product": "Producto",
  "unknown": "Otros",
};

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/whatsapp");
      setData(data);
    } catch {
      toast.error("No se pudieron cargar las métricas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/admin/analytics/whatsapp.csv", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `aurex-whatsapp-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo descargar el CSV");
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-600" /></div>;
  if (!data) return null;

  const maxDay = Math.max(1, ...data.daily.map((d) => d.count));
  const dayShort = (iso) => {
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold flex items-center gap-2">
          <BarChart3 size={20} className="text-[#ff8c3a]" /> Analítica de WhatsApp
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={downloadCsv} data-testid="analytics-csv" disabled={!data || data.total === 0} className="flex items-center gap-2 rounded-lg copper-bg px-3 py-2 text-xs font-semibold text-[#0a0a0c] disabled:opacity-50">
            <FileText size={14} /> Descargar CSV
          </button>
          <button onClick={load} data-testid="analytics-refresh" className="rounded-lg border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-white/30">Actualizar</button>
        </div>
      </div>
      <p className="text-sm text-zinc-500">Contá cuántas consultas por WhatsApp se disparan desde tu sitio, con desglose por origen (nav, botón flotante, tarjetas de contacto y productos).</p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: "Hoy", value: data.today, testid: "kpi-today" },
          { label: "Últimos 7 días", value: data.last_7_days, testid: "kpi-7d" },
          { label: "Últimos 30 días", value: data.last_30_days, testid: "kpi-30d" },
          { label: "Total", value: data.total, testid: "kpi-total" },
        ].map((k) => (
          <div key={k.label} data-testid={k.testid} className="rounded-2xl border border-white/10 bg-[#0e0e11] p-5">
            <div className="font-mono-jb text-[10px] uppercase tracking-[0.16em] text-zinc-500">{k.label}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-4xl font-extrabold text-zinc-100">{k.value}</span>
              <MessageCircle size={16} className="text-[#25D366]" />
            </div>
            <div className="mt-1 font-mono-jb text-[10px] uppercase tracking-[0.12em] text-zinc-600">clics</div>
          </div>
        ))}
      </div>

      {/* Daily chart */}
      <div className="rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
        <h4 className="mb-4 font-display text-base font-bold text-zinc-100">Últimos 14 días</h4>
        {data.total === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-500">Aún no se registraron clics. Los datos van a aparecer acá cuando alguien toque un botón de WhatsApp.</div>
        ) : (
          <div className="flex h-40 items-end gap-2" data-testid="analytics-chart">
            {data.daily.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-2">
                <div className="relative flex w-full flex-1 items-end">
                  <div
                    className="w-full rounded-t-lg copper-bg"
                    style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count > 0 ? "6px" : "1px", opacity: d.count > 0 ? 1 : 0.15 }}
                    title={`${d.day}: ${d.count} clics`}
                  />
                  {d.count > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-mono-jb text-[10px] text-zinc-400">{d.count}</span>
                  )}
                </div>
                <span className="font-mono-jb text-[9px] text-zinc-600">{dayShort(d.day)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* By source */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
          <h4 className="mb-4 font-display text-base font-bold text-zinc-100">Por origen (últimos 30 días)</h4>
          {data.by_source.length === 0 ? (
            <div className="py-6 text-sm text-zinc-500">Sin datos aún.</div>
          ) : (
            <ul className="space-y-2" data-testid="by-source-list">
              {data.by_source.map((s) => {
                const pct = data.last_30_days > 0 ? Math.round((s.count / data.last_30_days) * 100) : 0;
                return (
                  <li key={s.source} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-zinc-100">{SOURCE_LABEL[s.source] || s.source}</div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                        <div className="h-full copper-bg" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="font-display text-lg font-bold text-zinc-100">{s.count}</div>
                      <div className="font-mono-jb text-[10px] text-zinc-600">{pct}%</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0e0e11] p-6">
          <h4 className="mb-4 font-display text-base font-bold text-zinc-100">Productos más consultados</h4>
          {data.by_product.length === 0 ? (
            <div className="py-6 text-sm text-zinc-500">Todavía nadie consultó desde una tarjeta de producto.</div>
          ) : (
            <ul className="space-y-2" data-testid="by-product-list">
              {data.by_product.map((p) => (
                <li key={p.product} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
                  <span className="min-w-0 truncate text-sm text-zinc-100">{p.product}</span>
                  <span className="ml-4 font-display text-lg font-bold text-zinc-100">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
